import { useEffect } from 'react';

export interface UtmData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landingPage?: string;
  timestamp?: number;
}

const UTM_STORAGE_KEY = 'utm_data';

/**
 * Parse UTM parameters from URL search params
 */
function parseUtmParams(searchParams: URLSearchParams): UtmData {
  const utmData: UtmData = {};
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] satisfies (keyof UtmData)[];
  
  utmKeys.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      utmData[key] = value;
    }
  });
  
  return utmData;
}

/**
 * Check if any UTM parameter exists in the data
 */
function hasUtmParams(data: UtmData): boolean {
  return !!(
    data.utm_source ||
    data.utm_medium ||
    data.utm_campaign ||
    data.utm_term ||
    data.utm_content
  );
}

/**
 * Get stored UTM data from localStorage
 */
export function getStoredUtmData(): UtmData | null {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading UTM data from localStorage:', error);
  }
  return null;
}

/**
 * Hook to capture and store UTM parameters on first visit
 * Implements first-touch attribution - doesn't overwrite existing UTM data
 */
export function useUtmTracking() {
  useEffect(() => {
    try {
      // Check if we already have stored UTM data (first-touch attribution)
      const existingData = getStoredUtmData();
      
      // Parse current URL parameters
      const searchParams = new URLSearchParams(window.location.search);
      const currentUtmData = parseUtmParams(searchParams);
      
      // Only store if:
      // 1. We have new UTM params in the URL, OR
      // 2. No existing UTM data is stored yet
      const shouldStore = hasUtmParams(currentUtmData) || !existingData;
      
      if (shouldStore && !existingData) {
        const utmData: UtmData = {
          ...currentUtmData,
          referrer: document.referrer || undefined,
          landingPage: window.location.pathname + window.location.search,
          timestamp: Date.now(),
        };
        
        localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
        console.log('UTM data captured:', utmData);
      }
    } catch (error) {
      console.error('Error capturing UTM data:', error);
    }
  }, []); // Run only on mount
}
